/**
 * AIReportHistoryList — 환자 상세 페이지 내 AI 보고서 이력 목록
 * Patient.aiReviews + Redux store의 상세 데이터를 결합하여 표시
 * 줄 오른쪽 체크리스트 칸(설계 결정 20): 본인 완료 보고서에만 — 텍스트 기록 있음 「상담 체크리스트 작성됨」, 없음 「상담 체크리스트 작성」(둘 다 버튼).
 */

import React from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { COLOR_SYSTEM } from "components/design/design-system";
import Text from "components/atoms/Text";
import Icon from "components/atoms/Icon";
import { Tag } from "components/atoms";
import {
  AIReview,
  AIReviewStatus,
  ConsultationRecordListItem,
  getPromptTypeKoreanName,
} from "common/models/types";
import { RootState } from "store/index";

interface AIReportHistoryListProps {
  reports: AIReview[];
  patientId: number;
  /** 환자 상담 기록 목록(thunk 응답). 체크리스트 칸의 기록 유무를 reviewId·source로 판정한다. 미로드(undefined)면 칸을 그리지 않는다. */
  textRecords?: ConsultationRecordListItem[];
  /** 로그인한 약사 id — 본인 보고서(review.userId === 나)에만 칸을 그린다. */
  currentUserId?: string;
  /** 「작성」 — 그 보고서로 새 기록. 이 콜백과 onOpenChecklist가 모두 없으면 칸을 그리지 않는다. */
  onWriteChecklist?: (reviewId: number) => void;
  /** "작성됨" — 그 기록 열기. */
  onOpenChecklist?: (recordId: number) => void;
}

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RecordCard = styled.div`
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  border-radius: 8px;
  overflow: hidden;
`;

const RecordHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.2s;

  &:hover {
    background: ${COLOR_SYSTEM.get("Gray")?.[10]};
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const HeaderTextBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const HeaderTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

/** 줄 오른쪽 체크리스트 칸 — 폭을 고정해 줄마다 칸이 같은 자리에 서게 한다(두 문구 중 긴 쪽 기준). */
const ChecklistCell = styled.div`
  display: flex;
  justify-content: flex-end;
  min-width: 150px;
`;

/**
 * 체크리스트 버튼(사용자 지시 2026-10-13): 「상담 체크리스트 작성」(기록 없음) / 「상담 체크리스트 작성됨」(기록 있음) 둘 다 버튼꼴.
 * 호버는 줄 호버(회색 배경)와 구분되게 파란 테두리·옅은 파란 배경으로 준다.
 */
const ChecklistButton = styled.button<{ $done: boolean }>`
  height: 28px;
  padding: 0 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  border: 1px solid ${({ $done }) => ($done ? COLOR_SYSTEM.get("Gray")?.[100] : COLOR_SYSTEM.get("Blue")?.[500])};
  background: ${({ $done }) => ($done ? COLOR_SYSTEM.get("Gray")?.[10] : "#fff")};
  color: ${({ $done }) => ($done ? COLOR_SYSTEM.get("Gray")?.[600] : COLOR_SYSTEM.get("Blue")?.[500])};

  &:hover {
    border-color: ${COLOR_SYSTEM.get("Blue")?.[500]};
    background: ${COLOR_SYSTEM.get("Blue")?.[50]};
    color: ${COLOR_SYSTEM.get("Blue")?.[600]};
  }
`;

const MemoText = styled.div`
  font-size: 11px;
  color: ${COLOR_SYSTEM.get("Skyblue")?.[500]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;


function formatDateTime(date?: Date | string): string {
  if (!date) return "-";
  const d = new Date(date);
  const ymd = `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
  const hm = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return `${ymd} ${hm}`;
}


const AIReportHistoryList: React.FC<AIReportHistoryListProps> = ({
  reports,
  patientId,
  textRecords,
  currentUserId,
  onWriteChecklist,
  onOpenChecklist,
}) => {
  const navigate = useNavigate();
  const storeReviews = useSelector((state: RootState) => state.data.reviews);

  const sorted = [...reports].sort((a, b) => {
    const dateA = new Date(a.reviewDate || a.createdAt || 0).getTime();
    const dateB = new Date(b.reviewDate || b.createdAt || 0).getTime();
    return dateB - dateA;
  });

  if (sorted.length === 0) {
    return (
      <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[300]}>
        AI 보고서 이력이 없습니다.
      </Text>
    );
  }

  return (
    <ListContainer>
      {sorted.map((report) => {
        const reviewData = storeReviews[report.id] || report;
        const date = reviewData.reviewDate || reviewData.createdAt;
        const moduleName = getPromptTypeKoreanName(reviewData.promptType);

        const renderStatusTag = () => {
          const status = reviewData.status;
          if (status === AIReviewStatus.FAILED) {
            return <Tag variant="Tinted" color="Red" size="Small">실패</Tag>;
          }
          if (status === AIReviewStatus.PENDING && reviewData.queuePosition) {
            return <Tag variant="Tinted" color="Yellow" size="Small">대기중</Tag>;
          }
          if (status === AIReviewStatus.PROCESSING_LLM_ANALYSIS && reviewData.reviewResult) {
            return <Tag variant="Tinted" color="Green" size="Small">답변중</Tag>;
          }
          if ((status === AIReviewStatus.PENDING && !reviewData.queuePosition) ||
              status === AIReviewStatus.PROCESSING ||
              status === AIReviewStatus.PROCESSING_USER_INFO ||
              status === AIReviewStatus.PROCESSING_PRESCRIPTION ||
              status === AIReviewStatus.PROCESSING_DRUG_CRAWLING ||
              status === AIReviewStatus.PROCESSING_DRUG_INFO ||
              status === AIReviewStatus.PROCESSING_PROMPT_CREATION ||
              (status === AIReviewStatus.PROCESSING_LLM_ANALYSIS && !reviewData.reviewResult)) {
            return <Tag variant="Tinted" color="Blue" size="Small">처리중</Tag>;
          }
          if (status === AIReviewStatus.COMPLETED) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const reviewDate = new Date(reviewData.reviewDate || reviewData.createdAt || 0);
            reviewDate.setHours(0, 0, 0, 0);
            if (reviewDate.getTime() === today.getTime()) {
              return <Tag variant="Tinted" color="Green" size="Small">완료</Tag>;
            }
          }
          return null;
        };

        // 체크리스트 칸(결정 20): 본인 보고서 ∧ 완료일 때만. 기록 유무는 텍스트 기록 목록의 reviewId·source로.
        const renderChecklistCell = () => {
          if (!onWriteChecklist && !onOpenChecklist) return null;
          if (!textRecords || !currentUserId) return null;
          if (reviewData.userId !== currentUserId || reviewData.status !== AIReviewStatus.COMPLETED) {
            return null;
          }
          const record = textRecords.find((r) => r.source === "text" && r.reviewId === report.id);
          const button = record ? (
            onOpenChecklist && (
              <ChecklistButton type="button" $done onClick={() => onOpenChecklist(record.id)}>
                상담 체크리스트 작성됨
              </ChecklistButton>
            )
          ) : (
            onWriteChecklist && (
              <ChecklistButton type="button" $done={false} onClick={() => onWriteChecklist(report.id)}>
                상담 체크리스트 작성
              </ChecklistButton>
            )
          );
          if (!button) return null;
          // 칸 안 클릭은 줄 클릭(보고서 열기)으로 번지지 않게 막는다.
          return (
            <ChecklistCell data-testid="checklist-cell" onClick={(e) => e.stopPropagation()}>
              {button}
            </ChecklistCell>
          );
        };

        return (
          <RecordCard key={report.id} data-testid="ai-report-row" data-review-id={report.id}>
            <RecordHeader onClick={() => navigate(`/patients/${patientId}/reviews/${report.id}`)}>
              <HeaderLeft>
                <Icon
                  name={reviewData.pinnedAt ? "Pin 02 Filled" : "File"}
                  size={16}
                  color={
                    reviewData.pinnedAt
                      ? COLOR_SYSTEM.get("Yellow")?.[500]
                      : COLOR_SYSTEM.get("Gray")?.[500]
                  }
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <HeaderTextBox>
                  <HeaderTopRow>
                    <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[800]} style={{ fontWeight: 500 }}>
                      보고서 #{report.id}
                    </Text>
                    {renderStatusTag()}
                  </HeaderTopRow>
                  <span style={{ color: COLOR_SYSTEM.get("Gray")?.[500], fontSize: 11 }}>
                    {formatDateTime(date)} · {moduleName}
                  </span>
                  {reviewData.memo && (
                    <MemoText>{reviewData.memo}</MemoText>
                  )}
                </HeaderTextBox>
              </HeaderLeft>
              <HeaderRight>
                {renderChecklistCell()}
                <Icon name="Chevron Right" size={16} color={COLOR_SYSTEM.get("Gray")?.[400]} />
              </HeaderRight>
            </RecordHeader>
          </RecordCard>
        );
      })}
    </ListContainer>
  );
};

export default AIReportHistoryList;
